import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import {
  A11yLabelsProvider,
  AppThemeProvider,
  MuiLocaleProvider,
  SnackbarProvider,
  webTheme,
} from '@munifocus/ui';
import { initI18n } from '@munifocus/i18n';
import { queryClient } from '../lib/query-client';
import { ContactDialogProvider } from '../components/ContactDialog';
import { COOKIE_NAMES } from '../config/cookies';
import { CookieConsentProvider } from './CookieConsentProvider';
import { CookieBanner } from '../components/CookieBanner';
import { Analytics } from '../components/Analytics';

export function AppProviders({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Leave undefined unless explicitly configured, so initI18n can fall back to
    // the visitor's browser language instead of a hardcoded 'en'.
    const def = import.meta.env.VITE_DEFAULT_LOCALE as 'en' | 'fr' | undefined;
    // A `?lang=en|fr` query param (the target of our hreflang alternates) lets
    // crawlers and shared links land directly in a chosen locale; it wins over
    // the saved language cookie for this load.
    const qlang = new URLSearchParams(window.location.search).get('lang');
    const forced = qlang === 'en' || qlang === 'fr' ? (qlang as 'en' | 'fr') : undefined;
    void initI18n({ lng: def, fallbackLng: 'en', languageCookie: COOKIE_NAMES.LANGUAGE })
      .then(() => (forced ? i18next.changeLanguage(forced) : undefined))
      .then(() => setReady(true));
  }, []);

  const i18n = useMemo(() => i18next, []);

  if (!ready) return null;

  return (
    <AppThemeProvider theme={webTheme} cacheKey="web">
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <MuiLocaleProvider>
              <LocalizedA11yLabels>
                <LocalizedSnackbarProvider>
                  <CookieConsentProvider>
                    <BrowserRouter>
                      <ContactDialogProvider>{children}</ContactDialogProvider>
                      <CookieBanner />
                      <Analytics />
                    </BrowserRouter>
                  </CookieConsentProvider>
                </LocalizedSnackbarProvider>
              </LocalizedA11yLabels>
            </MuiLocaleProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </AppThemeProvider>
  );
}

/**
 * Feeds translated accessible names to the `@munifocus/ui` primitives that
 * render ARIA roles needing one (the loading spinners). Without this they fall
 * back to English, which would announce the French UI in English.
 */
function LocalizedA11yLabels({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const loading = t('common.a11y.loading');
  const labels = useMemo(() => ({ loading }), [loading]);
  return <A11yLabelsProvider value={labels}>{children}</A11yLabelsProvider>;
}

/** Feeds the localized dismiss label into the shared snackbar system. */
function LocalizedSnackbarProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return <SnackbarProvider dismissLabel={t('common.actions.dismiss')}>{children}</SnackbarProvider>;
}
